// Phase 118 — Contextual objective hint that fades in when flag state changes.
import { useEffect, useMemo, useRef, useState, type CSSProperties } from 'react';

type Vec3Like = { x?: number; y?: number; z?: number };

type FlagSnapshot = {
  team?: string | null;
  position?: Vec3Like;
  homePosition?: Vec3Like;
  holderId?: string | null;
  state?: string | null;
};

type ObjectiveHintBannerProps = {
  localPlayerId?: string;
  localTeam?: string | null;
  carryingFlagTeam?: string | null;
  flags: FlagSnapshot[];
};

function deriveHint(
  localPlayerId: string | undefined,
  localTeam: string | null | undefined,
  carryingFlagTeam: string | null | undefined,
  flags: FlagSnapshot[],
): { text: string; tone: string } | null {
  const t = (localTeam ?? '').toLowerCase();
  if (!t) return null;
  const ownFlag = flags.find((f) => (f.team ?? '').toLowerCase() === t);
  const enemyFlag = flags.find((f) => (f.team ?? '').toLowerCase() && (f.team ?? '').toLowerCase() !== t);

  if (carryingFlagTeam) {
    return { text: 'Capture enemy flag — return to your base!', tone: '#5fdd6a' };
  }
  if (ownFlag?.holderId) {
    return { text: 'Their carrier has your flag — hunt them down!', tone: '#ff5555' };
  }
  if (ownFlag && ownFlag.state && ownFlag.state.toLowerCase() === 'dropped') {
    return { text: 'Your flag is dropped — return it!', tone: '#ffcc66' };
  }
  if (enemyFlag?.holderId === localPlayerId) {
    // shouldn't hit due to carryingFlagTeam above, but safe
    return { text: 'Capture enemy flag — return to your base!', tone: '#5fdd6a' };
  }
  if (enemyFlag && enemyFlag.state && enemyFlag.state.toLowerCase() === 'dropped') {
    return { text: 'Enemy flag is dropped — grab it!', tone: '#62ddff' };
  }
  if (enemyFlag?.holderId) {
    return { text: 'Escort your flag carrier home!', tone: '#5fdd6a' };
  }
  return { text: 'Defend your flag · capture theirs', tone: 'rgba(231,244,252,0.65)' };
}

export function ObjectiveHintBanner({ localPlayerId, localTeam, carryingFlagTeam, flags }: ObjectiveHintBannerProps) {
  const hint = useMemo(() => deriveHint(localPlayerId, localTeam, carryingFlagTeam, flags),
    [localPlayerId, localTeam, carryingFlagTeam, flags]);
  const [opacity, setOpacity] = useState(0);
  const lastKeyRef = useRef<string | null>(null);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    const key = hint?.text ?? null;
    if (key === lastKeyRef.current) return;
    lastKeyRef.current = key;
    if (!key) {
      setOpacity(0);
      return;
    }
    setOpacity(1);
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => setOpacity(0.35), 3200);
  }, [hint?.text]);

  useEffect(() => () => { if (timerRef.current) clearTimeout(timerRef.current); }, []);

  if (!hint) return null;

  const style: CSSProperties = {
    position: 'absolute',
    top: 88,
    left: '50%',
    transform: 'translateX(-50%)',
    padding: '4px 14px',
    background: 'rgba(4,8,18,0.62)',
    border: `1px solid ${hint.tone}55`,
    color: hint.tone,
    fontFamily: 'monospace',
    fontSize: 11,
    letterSpacing: 1.8,
    textTransform: 'uppercase',
    pointerEvents: 'none',
    opacity,
    transition: 'opacity 0.6s ease',
    zIndex: 18,
    whiteSpace: 'nowrap',
  };

  return <div style={style}>{hint.text}</div>;
}
